import { Component, Input } from '@angular/core';
import { Affiliate } from 'src/app/models/Affiliate';
import { ClientCredits } from 'src/app/models/ClientCredits';
import { ManagedBalanceView, toBalanceView } from 'src/app/models/ManagedBalance';

/**
 * `Intl.NumberFormat` direto, como o `MoneyMicrosPipe` (mesmo motivo: o locale
 * `pt-BR` do Angular (pipe `currency`) nunca foi registrado com
 * `registerLocaleData` neste painel — usá-lo aqui quebraria em produção, não só
 * no teste). Criados uma vez por módulo: a lista tem até 25 linhas e a tabela de
 * Clientes do admin mais algumas, e recriar o formatter a cada leitura pesava.
 */
const BRL_FORMAT = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const INTEGER_FORMAT = new Intl.NumberFormat('pt-BR');

/**
 * Selo de saldo único para lista, gaveta e Estatísticas (decisão do mockup aprovado
 * em 27/10, CU-17tm466u6ga): recebe o payload cru de `/users/affiliates` ou de
 * `/partner/statistics/:id/credits` (mesmo formato do `/credits`) e não decide
 * regra nenhuma — só exibe o que `toBalanceView` já calculou.
 *
 * `mode="cell"`: linha compacta (lista, coluna de clientes do admin).
 * `mode="block"`: cartão grande com os canais (gaveta de detalhe, Estatísticas).
 */
@Component({
  selector: 'app-account-balance',
  templateUrl: './account-balance.component.html',
  styleUrls: ['./account-balance.component.scss'],
})
export class AccountBalanceComponent {
  @Input() mode: 'cell' | 'block' = 'cell';

  /**
   * O view-model é calculado UMA vez por troca de `source`, não a cada leitura do
   * template (o getter antigo rodava `toBalanceView` ~10 vezes por linha por ciclo).
   */
  view: ManagedBalanceView = toBalanceView({});
  walletAvailable: number | null = null;

  private current: Partial<Affiliate> & Partial<ClientCredits> = {};

  @Input()
  set source(value: Partial<Affiliate> & Partial<ClientCredits>) {
    this.current = value || {};
    this.view = toBalanceView(this.current);
    const raw = this.view.wallet?.available;
    this.walletAvailable = raw === null || raw === undefined ? null : Number(raw);
  }

  get source(): Partial<Affiliate> & Partial<ClientCredits> {
    return this.current;
  }

  formatBRL(value: number | null): string {
    if (value === null || value === undefined) {
      return '';
    }

    return BRL_FORMAT.format(value);
  }

  /**
   * O "≈ N" de SMS/ligação (achado do QA, 27/10): o R$ da carteira pode estar OK
   * mesmo quando o preço de UM canal falhou isoladamente — `sms.available`/
   * `call.available` vêm `null` nesse caso, sem o `balance_level` virar
   * `unavailable` (o saldo em si é conhecido). Concatenar `'≈ ' + null` na
   * interpolação do template virava o texto "≈ null"; aqui sempre "—".
   */
  formatEquivalent(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '—';
    }

    return INTEGER_FORMAT.format(value);
  }
}
